import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faImage, faLink, faPlus, faComments } from '@fortawesome/free-solid-svg-icons';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getExperienceTypeV2 } from '../../data/experienceTypesV2';
import toast from 'react-hot-toast';
import DecisionCardComponent from './DecisionCardComponent';
import DecisionCardDetailModal from './DecisionCardDetailModal';
import BottomSheet from '../BottomSheet';

export default function ExplorePhase({ experienceId, experience }) {
  const [decisionCards, setDecisionCards] = useState(experience.decisionCards || []);
  const [selectedCard, setSelectedCard] = useState(null);
  const [activeView, setActiveView] = useState('cards'); // 'cards' | 'gallery' | 'chat'
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  // Load decision cards from experience type defaults
  useEffect(() => {
    if (decisionCards.length === 0 && experience.typeId) {
      const typeDefaults = getExperienceTypeV2(experience.typeId);
      if (typeDefaults?.defaultDecisionCards) {
        const initializedCards = typeDefaults.defaultDecisionCards.map(card => ({
          ...card,
          status: 'to-discuss',
          kanbanColumn: 'to-discuss',
          gallery: [],
          comments: [],
          approvalRequired: experience.planningParticipants || [],
          approvedBy: []
        }));
        setDecisionCards(initializedCards);
        
        // Save to Firestore
        updateDoc(doc(db, 'projects', experienceId), {
          decisionCards: initializedCards,
          updatedAt: serverTimestamp()
        }).catch(err => console.error('Error initializing cards:', err));
      }
    }
  }, [experience.typeId, experienceId, experience.planningParticipants]);

  // Handle card updates
  const updateCard = async (cardId, updates) => {
    const updatedCards = decisionCards.map(card =>
      card.id === cardId ? { ...card, ...updates } : card
    );
    setDecisionCards(updatedCards);
    
    // Save to Firestore
    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        decisionCards: updatedCards,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating card:', error);
      toast.error('Failed to save changes');
    }
  };

  // Add custom decision card
  const addCustomCard = async (cardData) => {
    const newCard = {
      id: `custom-${Date.now()}`,
      ...cardData,
      status: 'to-discuss',
      kanbanColumn: 'to-discuss',
      gallery: [],
      comments: [],
      custom: true
    };
    
    const updatedCards = [...decisionCards, newCard];
    setDecisionCards(updatedCards);
    
    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        decisionCards: updatedCards,
        updatedAt: serverTimestamp()
      });
      toast.success('Decision card added');
      setShowAddCardModal(false);
    } catch (error) {
      console.error('Error adding card:', error);
      toast.error('Failed to add card');
    }
  };

  // Move to Discuss phase
  const proceedToDiscuss = async () => {
    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        currentPhase: 'discuss',
        updatedAt: serverTimestamp()
      });
      toast.success('Moving to discussion phase!');
    } catch (error) {
      console.error('Error moving to discuss phase:', error);
      toast.error('Failed to move to discussion phase');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* View tabs */}
      <div className="bg-white border-b border-border px-8 py-3">
        <div className="max-w-7xl mx-auto flex gap-4">
          <button
            onClick={() => setActiveView('cards')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              activeView === 'cards' 
                ? 'bg-primary text-white' 
                : 'text-text-secondary hover:bg-gray-50'
            }`}
          >
            Decision Cards
          </button>
          <button
            onClick={() => setActiveView('gallery')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              activeView === 'gallery' 
                ? 'bg-primary text-white' 
                : 'text-text-secondary hover:bg-gray-50'
            }`}
          >
            Inspiration Gallery
          </button>
          <button
            onClick={() => setActiveView('chat')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              activeView === 'chat' 
                ? 'bg-primary text-white' 
                : 'text-text-secondary hover:bg-gray-50'
            }`}
          >
            AI Assistant
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        <AnimatePresence mode="wait">
          {/* Decision Cards View */}
          {activeView === 'cards' && (
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-serif font-bold text-text-primary mb-2">
                    What needs to be decided?
                  </h1>
                  <p className="text-text-secondary">
                    Review these decision points and start clarifying your vision. Rate options, 
                    add images, and organize what needs discussion.
                  </p>
                </div>
                <button
                  onClick={() => setShowAddCardModal(true)}
                  className="px-4 py-2 bg-white border-2 border-border rounded-xl font-semibold hover:border-primary transition-all flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Decision
                </button>
              </div>

              {/* Decision cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {decisionCards
                  .filter(card => card.status !== 'dismissed')
                  .map(card => (
                    <DecisionCardComponent
                      key={card.id}
                      card={card}
                      onUpdate={(updates) => updateCard(card.id, updates)}
                      onClick={() => setSelectedCard(card)}
                    />
                  ))}
              </div>

              {/* Proceed button */}
              <div className="flex justify-end pt-8">
                <button
                  onClick={proceedToDiscuss}
                  className="bg-gradient-to-r from-primary to-primary-light text-white font-bold px-8 py-4 rounded-xl hover:shadow-lg transition-all"
                >
                  Move to Discussion Phase →
                </button>
              </div>
            </motion.div>
          )}

          {/* Inspiration Gallery View */}
          {activeView === 'gallery' && (
            <motion.div
              key="gallery"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-12"
            >
              <p className="text-text-secondary">Inspiration Gallery coming soon...</p>
            </motion.div>
          )}

          {/* AI Chat View */}
          {activeView === 'chat' && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-12"
            >
              <p className="text-text-secondary">AI Assistant coming soon...</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Decision Card Detail Modal */}
      <AnimatePresence>
        {selectedCard && (
          <DecisionCardDetailModal
            card={selectedCard}
            onClose={() => setSelectedCard(null)}
            onUpdate={(updates) => {
              updateCard(selectedCard.id, updates);
              setSelectedCard({ ...selectedCard, ...updates });
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
